import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';
import initI18n from './i18n';
import { translationsClient } from './lib/translations';
import { initAnalytics } from './lib/analytics/initAnalytics';
import { monitorNetworkStatus } from './utils/debugUtils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    }
  }
});

window.__APP_MOUNTED = false;

function getSwPaths(): { swPath: string; swScope: string } {
  const baseUrl = (window.HOTEL_CONFIG?.baseUrl || '').replace(/\/+$/, '');
  if (baseUrl) {
    return { swPath: `${baseUrl}/sw.js`, swScope: `${baseUrl}/` };
  }
  return { swPath: '/sw.js', swScope: '/' };
}

async function waitForSwActive(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return;

  const sw = registration.installing || registration.waiting;
  if (!sw) return;

  return new Promise<void>((resolve) => {
    if (sw.state === 'activated') {
      resolve();
      return;
    }
    sw.addEventListener('statechange', () => {
      if (sw.state === 'activated') resolve();
    });
    setTimeout(resolve, 5000);
  });
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  try {
    const { swPath, swScope } = getSwPaths();
    console.log('[SW] Registering:', swPath, 'scope:', swScope);

    const registration = await navigator.serviceWorker.register(swPath, { scope: swScope });
    await waitForSwActive(registration);

    console.log('[SW] Ready. Active:', !!registration.active, 'Scope:', registration.scope);
  } catch (err) {
    console.warn('[SW] Registration failed:', err);
  }
}

const init = async () => {
  try {
    if (window.__APP_MOUNTED) {
      return;
    }

    registerServiceWorker();

    await initAnalytics();
    monitorNetworkStatus();

    await Promise.all([
      translationsClient.getGeneralTranslations(),
      translationsClient.getCategoryTypeTranslations()
    ]);

    await initI18n();

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }

    const root = createRoot(rootElement);

    const baseUrl = window.HOTEL_CONFIG?.baseUrl || '';

    root.render(
      <React.StrictMode>
        <BrowserRouter basename={baseUrl}>
          <QueryClientProvider client={queryClient}>
            <App />
            {import.meta.env.DEV && <ReactQueryDevtools />}
          </QueryClientProvider>
        </BrowserRouter>
      </React.StrictMode>
    );

    window.__APP_MOUNTED = true;

  } catch (error) {
    console.error('Failed to initialize application:', error);
  }
};

init().catch(error => {
  console.error('Unhandled error in init function:', error);
});
